import { Card, CardActions, CardContent, Grid, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { useCallback, useEffect, useMemo } from 'react';
import Table from 'shell/components/shared/table';
import { PmiButton } from 'shell/components/shared/button';
import * as yup from 'yup';
import PmiTextField from 'shell/components/shared/text-field';
import { useNavigate, useParams } from 'react-router-dom';
import { Order } from 'shell/view-models/order';
import { authsObj, headCells } from './constants';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { PublishedConsumedEventClient } from './types';
import PmiTitle from 'shell/components/shared/title';
import { DateTimePicker } from '@mui/lab';
import { __OPERATION__EVENT__ADMIN__ } from 'shell/constants/environment';
import { ConsumedEvent } from 'shell/models/consumed-event';
import {
  ConsumedEventsResponse,
  transformConsumedEvents,
} from 'system/pages/search-published-events/utils/transform-consumed-events';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { transformDataset } from './utils';
import { DateTime } from 'luxon';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';

export interface PublishedEventForm {
  id?: number;
  eventTypeCode?: string;
  dateTimePublication?: DateTime | null;
  eventData?: string;
  eventDescription?: string;
  rowsPerPage?: number;
  page: number;
  orders: Order;
}

const validationSchema = yup.object().shape({
  id: yup.number().required('Il campo Id è obbligatorio').nullable(),
  eventTypeCode: yup.string().required('Il campo Tipo evento è obbligatorio'),
  dateTimePublication: yup.string().required('Il campo Data e ora pubblicazione è obbligatorio'),
  eventData: yup.string().required("Il campo Dati dell'evento è obbligatorio"),
});

interface FormPublishedEventProps {
  onOk: (form: PublishedEventForm) => void;
  onCancel: () => void;
  title: string;
  disabled?: boolean;
  initialValues?: PublishedEventForm;
  isEdit?: boolean;
}

const FormPublishedEvent = ({
  onOk,
  onCancel,
  title,
  disabled = true,
  initialValues,
  isEdit = false,
}: FormPublishedEventProps): React.ReactElement => {
  const onSubmit = useCallback(
    (formValues) => {
      onOk(formValues);
    },
    [onOk]
  );

  const params = useParams();
  const [eventsConsumed, getEventsConsumed] = useHttpFetch<
    HttpResponseList<ConsumedEvent>,
    ConsumedEventsResponse
  >(`${__OPERATION__EVENT__ADMIN__}/eventoConsumato/list?idEventoPubblicato=${params.id}`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: transformConsumedEvents,
  });

  const formPublishedEvent = useFormik<PublishedEventForm>({
    initialValues: initialValues || {
      id: undefined,
      eventData: '',
      eventTypeCode: '',
      dateTimePublication: null,
      rowsPerPage: 10,
      page: 0,
      orders: {},
    },
    validationSchema: validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  useEffect(() => {
    const queryString = transformDataset(formPublishedEvent.values);
    getEventsConsumed({
      params: {},
      paramsSerializer: () => queryString,
    });
  }, []);

  const handleChangePage = useCallback(
    (newPage) => {
      const newDataset = { ...formPublishedEvent.values, page: newPage || 0 };
      const queryString = transformDataset(newDataset);
      (
        getEventsConsumed({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<ConsumedEvent>, ConsumedEventsResponse>
      ).then(() => {
        formPublishedEvent.setFieldValue('page', newPage);
      });
    },
    [getEventsConsumed, formPublishedEvent.values, formPublishedEvent.setValues]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = { ...formPublishedEvent.values, rowsPerPage: rowsPerPage || 0, page: 0 };
      const queryString = transformDataset(newDataset);
      (
        getEventsConsumed({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<ConsumedEvent>, ConsumedEventsResponse>
      ).then(() => {
        formPublishedEvent.setValues(newDataset);
      });
    },
    [getEventsConsumed, formPublishedEvent.setFieldValue, formPublishedEvent.values]
  );

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...formPublishedEvent.values, orders };
      const queryString = transformDataset(newDataset);
      (
        getEventsConsumed({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<ConsumedEvent>, ConsumedEventsResponse>
      ).then(() => {
        formPublishedEvent.setFieldValue('orders', orders);
      });
    },
    [getEventsConsumed, formPublishedEvent.setFieldValue, formPublishedEvent.values]
  );

  const navigate = useNavigate();

  const onVisibilityPress = useCallback(
    (cell): Promise<PublishedConsumedEventClient> => {
      return new Promise((resolve) => {
        navigate(
          `/sistema/eventmanager/ricerca-eventi-pubblicati/dettaglio-evento-pubblicato/${params.id}/dettaglio-consumazione/${cell.id}`
        );
        resolve(cell);
      });
    },
    [navigate]
  );

  const auths = useActionAuthorization(authsObj);

  const count: number = useMemo(() => {
    return (eventsConsumed.data as ConsumedEventsResponse)?.pageInfo?.totalCount || 0;
  }, [eventsConsumed.data]);

  return (
    <Card raised className="pmi-my-3">
      <form onSubmit={formPublishedEvent.handleSubmit}>
        <CardContent style={{ padding: '24px' }}>
          <Typography gutterBottom variant="h5" component="h2" color="secondary">
            {title}
          </Typography>
          <div style={{ padding: '24px 0 0 0' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <PmiTextField
                  aria-label="ID evento"
                  label="ID evento"
                  id="id"
                  name="id"
                  type="text"
                  variant="outlined"
                  fullWidth
                  value={formPublishedEvent.values.id || ''}
                  disabled={disabled}
                  onChange={formPublishedEvent.handleChange}
                  error={formPublishedEvent.touched.id && Boolean(formPublishedEvent.errors.id)}
                  helperText={formPublishedEvent.errors.id}
                />
              </Grid>
              <Grid item xs={12} md={10}>
                <PmiTextField
                  aria-label="Tipo evento"
                  label="Tipo evento"
                  id="eventTypeCode"
                  name="eventTypeCode"
                  type="text"
                  variant="outlined"
                  fullWidth
                  value={formPublishedEvent.values.eventTypeCode || ''}
                  disabled={disabled}
                  onChange={formPublishedEvent.handleChange}
                  error={
                    formPublishedEvent.touched.eventTypeCode &&
                    Boolean(formPublishedEvent.errors.eventTypeCode)
                  }
                  helperText={formPublishedEvent.errors.eventTypeCode}
                />
              </Grid>
              <Grid item xs={12} md={10}>
                <PmiTextField
                  aria-label="Descrizione Tipo evento"
                  label="Descrizione Tipo evento"
                  id="eventDescription"
                  name="eventDescription"
                  type="text"
                  variant="outlined"
                  fullWidth
                  value={formPublishedEvent.values.eventDescription || ''}
                  disabled={disabled}
                  onChange={formPublishedEvent.handleChange}
                  error={
                    formPublishedEvent.touched.eventDescription &&
                    Boolean(formPublishedEvent.errors.eventDescription)
                  }
                  helperText={formPublishedEvent.errors.eventDescription}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="Data e ora pubblicazione"
                  aria-label="Data e ora pubblicazione"
                  disabled
                  value={formPublishedEvent.values.dateTimePublication || null}
                  onChange={(date) => formPublishedEvent.setFieldValue('dateTimePublication', date)}
                  renderInput={(parameters) => (
                    <PmiTextField
                      {...parameters}
                      id="dateTimePublication"
                      error={
                        formPublishedEvent.touched.dateTimePublication &&
                        Boolean(formPublishedEvent.errors.dateTimePublication)
                      }
                      helperText={formPublishedEvent.errors.dateTimePublication}
                      fullWidth
                      disabled
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={10}>
                <PmiTextField
                  aria-label="Dati dell'evento"
                  label="Dati dell'evento"
                  id="eventData"
                  name="eventData"
                  type="text"
                  multiline
                  rows={4}
                  variant="outlined"
                  fullWidth
                  value={formPublishedEvent.values.eventData || ''}
                  disabled={disabled || isEdit}
                  onChange={formPublishedEvent.handleChange}
                  error={
                    formPublishedEvent.touched.eventData &&
                    Boolean(formPublishedEvent.errors.eventData)
                  }
                  helperText={formPublishedEvent.errors.eventData}
                />
              </Grid>
            </Grid>
            <PmiTitle title="Consumazione Evento" />
            {eventsConsumed.data ? (
              (eventsConsumed.data as ConsumedEventsResponse)?.consumedEvents?.length > 0 ? (
                <Table
                  onSort={onSort}
                  head={headCells}
                  isServer
                  orders={formPublishedEvent.values.orders}
                  activeActions={auths}
                  page={formPublishedEvent.values.page}
                  rowsPerPage={formPublishedEvent.values.rowsPerPage}
                  body={(eventsConsumed.data as ConsumedEventsResponse)?.consumedEvents}
                  onVisibilityPress={onVisibilityPress}
                  count={count || 0}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ariaLabel="Consumazione evento"
                />
              ) : (
                <Typography color="secondary.main">
                  Nessun elemento trovato con la ricerca effettuata.
                </Typography>
              )
            ) : undefined}
          </div>
        </CardContent>
        <CardActions style={{ padding: '24px 24px 16px 24px' }}>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => {
              onCancel();
            }}
          >
            Indietro
          </PmiButton>
        </CardActions>
      </form>
    </Card>
  );
};

FormPublishedEvent.displayName = 'Form Published Event component';

export default FormPublishedEvent;
